import { Component, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { ITenantUser } from '../../../../../../shared/interfaces/tenant.interface';
import { IModal } from '../../../../components/modals/modal.interface';
import { TenantService } from '../../../../services/tenant.service';
import { ModalContainerComponent } from '../../../../components/modals/modal-container.component';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { $tenantUser, $tenantUserIntegrations } from '../../../../../../shared/constants/tenant';
import { $user } from '../../../../../../shared/constants/user';
import { NotificationService } from '../../../../services/notification.service';
import { NotificationPosition } from '../../../../models/notification.model';
import { TranslateService } from '../../../../translate/translate.service';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { AcademyUserRole } from '../../../../../../shared/constants/academy-user';

export enum AssignAcademyRolesFormFields {
  roles = 'roles',
}

@Component({
  selector: 'app-assign-academy-roles-modal',
  templateUrl: './assign-academy-roles-modal.component.html',
  styleUrls: ['./assign-academy-role-modal.component.scss'],
  standalone: false,
})
export class AssignAcademyRolesModalComponent implements IModal, OnDestroy {
  private user: ITenantUser;
  private subscriptions: Subscription;

  @ViewChild(ModalContainerComponent, { static: true })
  public container: ModalContainerComponent;

  public form: UntypedFormGroup;
  public $roles: typeof AcademyUserRole;
  public submitted: boolean;

  constructor(
    private tenantService: TenantService,
    formGroup: UntypedFormBuilder,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private loadErrorHandler: LoadErrorHandler,
  ) {
    this.form = formGroup.group({
      [AssignAcademyRolesFormFields.roles]: formGroup.control('', CustomValidators.required),
    });

    this.submitted = false;
    this.$roles = AcademyUserRole;
    this.subscriptions = new Subscription();
  }

  private onSetUserRolesSuccess(): void {
    this.notificationService.success(this.translateService.instant('assign_academy_roles_success'), NotificationPosition.Toast);
  }

  private onSetUserRolesError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('assign_academy_roles_error', error, AssignAcademyRolesModalComponent.name);
  }

  private setFormRole(user: ITenantUser): void {
    let roles: AcademyUserRole | AcademyUserRole[] =
      user?.[$tenantUser.integrations]?.[$tenantUserIntegrations.academy]?.roles ?? [];

    if (!Array.isArray(roles)) {
      roles = [roles];
    }

    this.form.get(AssignAcademyRolesFormFields.roles).setValue(roles);
  }

  public init(user: ITenantUser): void {
    this.user = user;
    this.setFormRole(user);
  }

  public get changeRolesDescription(): string {
    return this.translateService.instant(
      'change_academy_roles_select_description',
      this.user[$tenantUser.user][$user.displayName],
    );
  }

  public mapRoleValueToNameKey(roleValue: AcademyUserRole): string {
    if (roleValue === AcademyUserRole.manager) {
      return 'client_manager';
    }
    return 'learner';
  }

  public onSubmit(): void {
    this.subscriptions.add(
      this.tenantService
        .setUserRolesInAcademy(
          this.user[$tenantUser.tenantId],
          this.user[$tenantUser.userId],
          this.form.get(AssignAcademyRolesFormFields.roles).value,
        )
        .subscribe({
          next: () => {
            this.submitted = true;
            this.container.closeModal();
            this.onSetUserRolesSuccess();
          },
          error: this.onSetUserRolesError.bind(this),
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
